import { forwardRef } from "react";
import { format } from "date-fns";
import type { SupplierOrder } from "@/types/supplierOrders";

interface SupplierData {
  address_line1?: string;
  address_line2?: string;
  postal_code?: string;
  city?: string;
  province?: string;
  email?: string;
  phone?: string;
  contact_name?: string;
}

interface SupplierOrderPDFProps {
  order: SupplierOrder & { supplier?: SupplierData };
  language?: "es" | "en";
  hideValues?: boolean;
}

const TEXTS = {
  es: {
    title: "PEDIDO DE COMPRA",
    titleEn: "PURCHASE ORDER",
    orderNumber: "Pedido Nº",
    date: "Fecha",
    expectedDate: "Entrega prevista",
    pages: "Página",
    supplier: "PROVEEDOR",
    taxId: "CIF/NIF",
    warehouse: "Almacén",
    payment: "Forma de pago",
    code: "Código",
    description: "Descripción",
    qty: "Cant.",
    unit: "Ud.",
    unitPrice: "Precio Ud.",
    discount: "Dto.",
    subtotal: "Subtotal",
    taxBreakdown: "DESGLOSE IMPUESTOS",
    taxType: "Tipo",
    taxBase: "Base",
    taxAmount: "IVA",
    observations: "OBSERVACIONES",
    subtotalLabel: "Subtotal",
    taxLabel: "IVA",
    totalLabel: "TOTAL",
    approval: "Aprobado por",
    manager: "Francisco Marín",
    position: "CEO - Gerencia",
    receiptTitle: "ALBARÁN DE RECEPCIÓN",
    receiptTitleEn: "DELIVERY RECEIPT",
    footerNote: "Es imprescindible el envío de certificados de conformidad y fichas técnicas de los materiales suministrados.",
    footerNoteEn: "Certificates of conformity and technical data sheets of supplied materials are required.",
  },
  en: {
    title: "PURCHASE ORDER",
    titleEn: "",
    orderNumber: "Order No.",
    date: "Date",
    expectedDate: "Expected delivery",
    pages: "Page",
    supplier: "SUPPLIER",
    taxId: "Tax ID",
    warehouse: "Warehouse",
    payment: "Payment terms",
    code: "Code",
    description: "Description",
    qty: "Qty.",
    unit: "Unit",
    unitPrice: "Unit Price",
    discount: "Disc.",
    subtotal: "Subtotal",
    taxBreakdown: "TAX BREAKDOWN",
    taxType: "Rate",
    taxBase: "Base",
    taxAmount: "VAT",
    observations: "OBSERVATIONS",
    subtotalLabel: "Subtotal",
    taxLabel: "VAT",
    totalLabel: "TOTAL",
    approval: "Approved by",
    manager: "Francisco Marín",
    position: "CEO - Management",
    receiptTitle: "DELIVERY RECEIPT",
    receiptTitleEn: "",
    footerNote: "Certificates of conformity and technical data sheets of supplied materials are required.",
    footerNoteEn: "",
  },
};

const COLORS = {
  primary: "#1a365d",
  secondary: "#2d4a6f",
  accent: "#3182ce",
  light: "#ebf4ff",
  border: "#cbd5e0",
  text: "#1a202c",
  textMuted: "#4a5568",
  white: "#ffffff",
};

export const SupplierOrderPDF = forwardRef<HTMLDivElement, SupplierOrderPDFProps>(
  ({ order, language = "es", hideValues = false }, ref) => {
    const t = TEXTS[language];

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount) + " €";
    };

    const formatNumber = (num: number) => {
      return new Intl.NumberFormat("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
    };

    const taxBreakdown: Record<number, { base: number; tax: number }> = {};
    let totalBase = 0;
    let totalTax = 0;
    let totalBruto = 0;
    let totalDescuento = 0;
    (order.lines || []).forEach((line) => {
      const rate = line.tax_rate || 21;
      const bruto = line.quantity_ordered * line.unit_price;
      const discountPct = (line as any).discount_percent || 0;
      const descuento = bruto * discountPct / 100;
      const lineBase = line.subtotal || (bruto - descuento);
      const lineTax = line.tax_amount || (lineBase * rate / 100);
      if (!taxBreakdown[rate]) {
        taxBreakdown[rate] = { base: 0, tax: 0 };
      }
      taxBreakdown[rate].base += lineBase;
      taxBreakdown[rate].tax += lineTax;
      totalBruto += bruto;
      totalDescuento += descuento;
      totalBase += lineBase;
      totalTax += lineTax;
    });

    const docTitle = hideValues ? t.receiptTitle : t.title;
    const docTitleEn = hideValues ? t.receiptTitleEn : t.titleEn;

    return (
      <div
        ref={ref}
        className="bg-white text-black print:text-black"
        style={{
          width: "210mm",
          minHeight: "297mm",
          fontFamily: "'Segoe UI', Arial, sans-serif",
          fontSize: "9pt",
          padding: "12mm 15mm",
          position: "relative",
          boxSizing: "border-box",
          color: COLORS.text,
        }}
      >
        {/* HEADER */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8mm", paddingBottom: "5mm", borderBottom: `2px solid ${COLORS.primary}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: "4mm" }}>
            <img
              src="/perseo/logoMagma.png"
              alt="Magma Composites"
              style={{ height: "18mm", objectFit: "contain" }}
              crossOrigin="anonymous"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
            <div style={{ width: "1px", height: "15mm", backgroundColor: COLORS.border, margin: "0 2mm" }} />
            <img
              src="/perseo/logoISO.png"
              alt="ISO 9001"
              style={{ height: "14mm", objectFit: "contain" }}
              crossOrigin="anonymous"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          </div>

          <div style={{ textAlign: "right", fontSize: "8pt", color: COLORS.textMuted }}>
            <div style={{ fontWeight: "700", fontSize: "11pt", color: COLORS.primary, marginBottom: "1mm" }}>MAGMA COMPOSITES S.L.</div>
            <div>Pol. Ind. Cueva San José II, Nave nº8</div>
            <div>44570 Calanda, Teruel, España</div>
            <div>CIF: ESB63222715 | Tel: 978 877 816</div>
          </div>
        </div>

        {/* TITULO */}
        <div style={{ textAlign: "center", marginBottom: "6mm" }}>
          <div style={{
            display: "inline-block",
            backgroundColor: COLORS.primary,
            color: COLORS.white,
            padding: "3mm 12mm",
            fontSize: "14pt",
            fontWeight: "700",
            letterSpacing: "0.5mm",
            borderRadius: "1mm",
          }}>
            {docTitle}
            {docTitleEn && <span style={{ fontWeight: "400", marginLeft: "3mm", opacity: 0.8 }}>/ {docTitleEn}</span>}
          </div>
        </div>

        {/* INFO PEDIDO + PROVEEDOR */}
        <div style={{ display: "flex", justifyContent: "space-between", gap: "8mm", marginBottom: "6mm" }}>
          <div style={{ flex: 1, backgroundColor: COLORS.light, padding: "4mm", borderRadius: "2mm", borderLeft: `3px solid ${COLORS.primary}` }}>
            <div style={{ fontSize: "7pt", color: COLORS.textMuted, textTransform: "uppercase", marginBottom: "1mm" }}>{t.supplier}</div>
            <div style={{ fontSize: "12pt", fontWeight: "600", color: COLORS.primary }}>{order.supplier_name}</div>
            {order.supplier_tax_id && (
              <div style={{ fontSize: "9pt", color: COLORS.textMuted, marginTop: "1mm" }}>{t.taxId}: {order.supplier_tax_id}</div>
            )}
            {/* Dirección del proveedor */}
            {order.supplier?.address_line1 && (
              <div style={{ fontSize: "8pt", color: COLORS.text, marginTop: "2mm" }}>{order.supplier.address_line1}</div>
            )}
            {order.supplier?.address_line2 && (
              <div style={{ fontSize: "8pt", color: COLORS.text }}>{order.supplier.address_line2}</div>
            )}
            {(order.supplier?.postal_code || order.supplier?.city) && (
              <div style={{ fontSize: "8pt", color: COLORS.text }}>
                {order.supplier.postal_code} {order.supplier.city}
                {order.supplier?.province && `, ${order.supplier.province}`}
              </div>
            )}
            {/* Email del proveedor */}
            {order.supplier?.email && (
              <div style={{ fontSize: "8pt", color: COLORS.accent, marginTop: "1mm" }}>{order.supplier.email}</div>
            )}
            {/* Teléfono del proveedor */}
            {order.supplier?.phone && (
              <div style={{ fontSize: "8pt", color: COLORS.textMuted }}>Tel: {order.supplier.phone}</div>
            )}
          </div>

          <div style={{ width: "50mm", textAlign: "right" }}>
            <div style={{
              backgroundColor: COLORS.primary,
              color: COLORS.white,
              padding: "2mm 4mm",
              borderRadius: "1mm",
              marginBottom: "2mm",
              display: "inline-block",
            }}>
              <span style={{ fontSize: "8pt" }}>{t.orderNumber}</span>
              <span style={{ fontSize: "14pt", fontWeight: "700", marginLeft: "2mm" }}>{order.order_number}</span>
            </div>
            <div style={{ fontSize: "9pt", marginTop: "2mm" }}>
              <div><span style={{ color: COLORS.textMuted }}>{t.date}:</span> <strong>{format(new Date(order.order_date), "dd/MM/yyyy")}</strong></div>
              {order.expected_date && (
                <div style={{ marginTop: "1mm" }}>
                  <span style={{ color: COLORS.textMuted }}>{t.expectedDate}:</span> <strong>{format(new Date(order.expected_date), "dd/MM/yyyy")}</strong>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* TABLA DE LINEAS */}
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "6mm" }}>
          <thead>
            <tr style={{ backgroundColor: COLORS.primary, color: COLORS.white }}>
              <th style={{ padding: "2.5mm 2mm", textAlign: "left", fontWeight: "600", fontSize: "8pt", width: "15%" }}>{t.code}</th>
              <th style={{ padding: "2.5mm 2mm", textAlign: "left", fontWeight: "600", fontSize: "8pt", width: hideValues ? "55%" : "30%" }}>{t.description}</th>
              <th style={{ padding: "2.5mm 2mm", textAlign: "center", fontWeight: "600", fontSize: "8pt", width: "8%" }}>{t.qty}</th>
              <th style={{ padding: "2.5mm 2mm", textAlign: "center", fontWeight: "600", fontSize: "8pt", width: "7%" }}>{t.unit}</th>
              {!hideValues && (
                <>
                  <th style={{ padding: "2.5mm 2mm", textAlign: "right", fontWeight: "600", fontSize: "8pt", width: "12%" }}>{t.unitPrice}</th>
                  <th style={{ padding: "2.5mm 2mm", textAlign: "center", fontWeight: "600", fontSize: "8pt", width: "8%" }}>{t.discount}</th>
                  <th style={{ padding: "2.5mm 2mm", textAlign: "right", fontWeight: "600", fontSize: "8pt", width: "12%" }}>{t.subtotal}</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {(order.lines || []).map((line, idx) => (
              <tr key={line.id || idx} style={{ backgroundColor: idx % 2 === 0 ? COLORS.white : "#f8fafc", borderBottom: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: "2mm", fontSize: "8pt", color: COLORS.textMuted }}>{line.code || "-"}</td>
                <td style={{ padding: "2mm", fontSize: "9pt" }}>{line.description}</td>
                <td style={{ padding: "2mm", textAlign: "center", fontWeight: "500" }}>{formatNumber(line.quantity_ordered)}</td>
                <td style={{ padding: "2mm", textAlign: "center", fontSize: "8pt", color: COLORS.textMuted }}>{line.unit}</td>
                {!hideValues && (
                  <>
                    <td style={{ padding: "2mm", textAlign: "right", fontFamily: "monospace" }}>{formatCurrency(line.unit_price)}</td>
                    <td style={{ padding: "2mm", textAlign: "center", color: (line as any).discount_percent ? COLORS.accent : COLORS.textMuted }}>
                      {(line as any).discount_percent ? `${(line as any).discount_percent}%` : "-"}
                    </td>
                    <td style={{ padding: "2mm", textAlign: "right", fontWeight: "500", fontFamily: "monospace" }}>{formatCurrency(line.subtotal || line.quantity_ordered * line.unit_price)}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {/* OBSERVACIONES + TOTALES */}
        <div style={{ display: "flex", justifyContent: "space-between", gap: "10mm", marginBottom: "8mm" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "8pt", fontWeight: "600", color: COLORS.primary, marginBottom: "2mm", textTransform: "uppercase" }}>{t.observations}</div>
            <div style={{
              backgroundColor: "#fffbeb",
              padding: "3mm",
              borderRadius: "2mm",
              fontSize: "8pt",
              minHeight: "15mm",
              borderLeft: "3px solid #f59e0b",
              whiteSpace: "pre-line",
            }}>
              {order.notes || "-"}
            </div>
          </div>

          {!hideValues && (
            <div style={{ width: "70mm" }}>
              <div style={{ marginBottom: "3mm" }}>
                {/* Importe bruto */}
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "8pt", color: COLORS.textMuted, marginBottom: "1mm" }}>
                  <span>Importe bruto:</span>
                  <span style={{ fontFamily: "monospace" }}>{formatCurrency(totalBruto)}</span>
                </div>
                {/* Descuento total */}
                {totalDescuento > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "8pt", color: COLORS.accent, marginBottom: "1mm", fontWeight: "600" }}>
                    <span>Descuento total:</span>
                    <span style={{ fontFamily: "monospace" }}>-{formatCurrency(totalDescuento)}</span>
                  </div>
                )}
                {/* Separador */}
                <div style={{ borderBottom: `1px dashed ${COLORS.border}`, margin: "2mm 0" }} />
                {Object.entries(taxBreakdown).map(([rate, values]) => (
                  <div key={rate} style={{ display: "flex", justifyContent: "space-between", fontSize: "8pt", color: COLORS.textMuted, marginBottom: "1mm" }}>
                    <span>Base {rate}%:</span>
                    <span style={{ fontFamily: "monospace" }}>{formatCurrency(values.base)}</span>
                  </div>
                ))}
                {Object.entries(taxBreakdown).map(([rate, values]) => (
                  <div key={`tax-${rate}`} style={{ display: "flex", justifyContent: "space-between", fontSize: "8pt", color: COLORS.textMuted, marginBottom: "1mm" }}>
                    <span>IVA {rate}%:</span>
                    <span style={{ fontFamily: "monospace" }}>{formatCurrency(values.tax)}</span>
                  </div>
                ))}
              </div>

              <div style={{
                backgroundColor: COLORS.primary,
                color: COLORS.white,
                padding: "3mm 4mm",
                borderRadius: "2mm",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}>
                <span style={{ fontWeight: "600", fontSize: "10pt" }}>{t.totalLabel}</span>
                <span style={{ fontWeight: "700", fontSize: "14pt", fontFamily: "monospace" }}>{formatCurrency(order.total_amount || (totalBase + totalTax))}</span>
              </div>
            </div>
          )}
        </div>

        {/* FIRMA */}
        {!hideValues && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "15mm" }}>
            <div style={{
              width: "55mm",
              textAlign: "center",
              padding: "3mm",
              border: `1px solid ${COLORS.border}`,
              borderRadius: "2mm",
            }}>
              <div style={{ fontSize: "7pt", color: COLORS.textMuted, textTransform: "uppercase", marginBottom: "2mm" }}>{t.approval}</div>
              <img
                src="/perseo/firma-ceo.png"
                alt="Firma"
                style={{ height: "15mm", maxWidth: "45mm", objectFit: "contain" }}
                crossOrigin="anonymous"
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              />
              <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: "2mm", marginTop: "2mm" }}>
                <div style={{ fontWeight: "600", fontSize: "9pt", color: COLORS.primary }}>{t.manager}</div>
                <div style={{ fontSize: "7pt", color: COLORS.textMuted }}>{t.position}</div>
              </div>
            </div>
          </div>
        )}

        {/* FOOTER */}
        <div style={{
          position: "absolute",
          bottom: "10mm",
          left: "15mm",
          right: "15mm",
          borderTop: `1px solid ${COLORS.border}`,
          paddingTop: "3mm",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ fontSize: "7pt", color: COLORS.textMuted, maxWidth: "70%" }}>
              {t.footerNote}
              {language === "es" && t.footerNoteEn && (
                <div style={{ marginTop: "1mm", fontStyle: "italic" }}>{t.footerNoteEn}</div>
              )}
            </div>
            <div style={{ fontSize: "7pt", color: COLORS.textMuted, textAlign: "right" }}>
              <div style={{ fontWeight: "600", color: COLORS.primary }}>MAGMA COMPOSITES S.L.</div>
              <div>www.magmacomposites.com</div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

SupplierOrderPDF.displayName = "SupplierOrderPDF";
